import { gql, type TypedDocumentNode } from '@apollo/client';
import type { ComponentProps } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import {
  ManagerProgressionTaskState,
  StepState,
} from '@sorare/core/src/__generated__/globalTypes';
import { ShieldButton } from '@sorare/core/src/atoms/buttons/ShieldButton';
import { Dropdown } from '@sorare/core/src/atoms/dropdowns/Dropdown';
import { Badge } from '@sorare/core/src/atoms/ui/Badge/index.tsx';
import { withFragments } from '@sorare/core/src/lib/gql';

import { PickerHeader } from '#src/components/pickers/PickerHeader/index.tsx';
import { PickerOptionsGrid } from '#src/components/pickers/PickerOptionsGrid.tsx';
import { useLastVisitedBoardView } from '#src/hooks/useLastVisitedBoardView.ts';
import { Mode } from '#src/lib/gameMode.ts';

import { BoardPickerOption, careerProSteps } from './BoardPickerOption.tsx';
import type {
  BoardPicker_careerBoard,
  boardsWithCareerProSteps_careerBoard,
} from './__generated__/index.graphql.ts';

const BoardIcon = styled.img`
  width: var(--quadruple-unit);
  height: var(--quadruple-unit);
  object-fit: contain;

  &.sm {
    margin-top: 0;
    width: 20px;
    height: 20px;
  }

  &.md {
    margin-top: 0;
    width: var(--triple-unit);
    height: var(--triple-unit);
  }
`;

const DropdownContent = styled.div`
  background: var(--c-black);
`;

type Props = {
  boards: readonly BoardPicker_careerBoard[];
  currentBoardId?: string;
  onSelect: (params: { boardId: string; taskId: string }) => void;
  highlighted?: boolean;
} & Pick<ComponentProps<typeof ShieldButton>, 'orientation' | 'size'>;

export const boardsWithCareerProSteps = withFragments(
  <T extends boardsWithCareerProSteps_careerBoard>(boards: readonly T[]) =>
    boards.filter((b): b is T => careerProSteps(b).length > 0),
  {
    careerBoard: gql`
      fragment boardsWithCareerProSteps_careerBoard on CareerBoard {
        id
        ...careerProSteps_careerBoard
      }
      ${careerProSteps.fragments.careerBoard}
    ` as TypedDocumentNode<boardsWithCareerProSteps_careerBoard>,
  }
);

export const BoardPicker = ({
  boards,
  currentBoardId,
  onSelect,
  size = 'md',
  highlighted,
  orientation,
}: Props) => {
  const { updateBoardView } = useLastVisitedBoardView();
  const boardsWithSteps = boardsWithCareerProSteps(boards);
  const currentBoard = boardsWithSteps.find(b => b.id === currentBoardId);

  if (boardsWithSteps.length === 0) {
    return null;
  }

  const handleSelectBoard = (boardId: string, closeDropdown: () => void) => {
    const board = boardsWithSteps.find(b => b.id === boardId);
    if (!board) return;

    const steps = careerProSteps(board);
    // Fall back to the first step so a board with only failed/canceled steps
    // stays navigable instead of silently doing nothing.
    const taskId =
      steps.find(s => {
        const state = s.myTask.aasmState;
        return (
          state === ManagerProgressionTaskState.READY ||
          state === ManagerProgressionTaskState.COMPLETED
        );
      })?.myTask.id ?? steps[0]?.myTask.id;
    if (!taskId) return;

    if (board.title) {
      updateBoardView({
        lastVisitedMode: Mode.PRO,
        lastVisitedBoardTitle: board.title,
      });
    }
    onSelect({ boardId: board.id, taskId });
    closeDropdown();
  };

  const hasClaimableBoard = boardsWithSteps.some(
    board =>
      board.myCurrentStep?.state === StepState.CLAIMABLE ||
      board.myUnclaimedCollectionTasksCount > 0
  );

  return (
    <Dropdown
      align="center"
      gap={8}
      label={props => (
        <Badge
          badgeContent={
            !!hasClaimableBoard && (
              <span style={{ transform: 'rotate(15deg)' }}>!</span>
            )
          }
          size="small"
          overlap="circular"
        >
          <ShieldButton
            type="button"
            aria-label={currentBoard?.title ?? undefined}
            size={size}
            icon={
              currentBoard?.iconUrl ? (
                <BoardIcon
                  className={size}
                  src={currentBoard.iconUrl}
                  alt=""
                  role="presentation"
                />
              ) : undefined
            }
            orientation={orientation}
            borderColor={
              highlighted ? (currentBoard?.color ?? undefined) : undefined
            }
            {...props}
          />
        </Badge>
      )}
    >
      {({ closeDropdown }) => (
        <DropdownContent>
          <PickerHeader onClose={closeDropdown}>
            <FormattedMessage defaultMessage="Choose League" />
          </PickerHeader>
          <PickerOptionsGrid>
            {boardsWithSteps.map(board => (
              <BoardPickerOption
                key={board.id}
                board={board}
                selected={board.id === currentBoardId}
                onClick={() => handleSelectBoard(board.id, closeDropdown)}
              />
            ))}
          </PickerOptionsGrid>
        </DropdownContent>
      )}
    </Dropdown>
  );
};

BoardPicker.fragments = {
  careerBoard: gql`
    fragment BoardPicker_careerBoard on CareerBoard {
      id
      color(variant: PRIMARY)
      tileBackgroundUrl
      myUnclaimedCollectionTasksCount
      myCurrentStep {
        id
        state
      }
      ...BoardPickerOption_careerBoard
      ...boardsWithCareerProSteps_careerBoard
    }
    ${BoardPickerOption.fragments.careerBoard}
    ${boardsWithCareerProSteps.fragments.careerBoard}
  ` as TypedDocumentNode<BoardPicker_careerBoard>,
};
